// Geometry of links between cards: anchor sides, Bézier curve, dashes and hit testing.
// Used by the renderer (drawing) and the app (clicking a line), so both always agree.

export interface Box { x: number; y: number; w: number; h: number }
type P = [number, number];

/**
 * Curve from box `a` to box `b`. The sides face each other along the dominant axis: a card to
 * the right connects right edge → left edge, a card below bottom → top, and so on.
 */
export function curve(a: Box, b: Box): [P, P, P, P] {
  const ax = a.x + a.w / 2, ay = a.y + a.h / 2, bx = b.x + b.w / 2, by = b.y + b.h / 2;
  const dx = bx - ax, dy = by - ay;
  let p0: P, p3: P, n0: P, n3: P;
  if (Math.abs(dx) >= Math.abs(dy)) {
    const s = dx >= 0 ? 1 : -1;
    p0 = [ax + (s * a.w) / 2, ay];
    p3 = [bx - (s * b.w) / 2, by];
    n0 = [s, 0];
    n3 = [-s, 0];
  } else {
    const s = dy >= 0 ? 1 : -1;
    p0 = [ax, ay + (s * a.h) / 2];
    p3 = [bx, by - (s * b.h) / 2];
    n0 = [0, s];
    n3 = [0, -s];
  }
  const d = Math.hypot(p3[0] - p0[0], p3[1] - p0[1]);
  const k = Math.min(220, Math.max(30, d * 0.45));
  return [p0, [p0[0] + n0[0] * k, p0[1] + n0[1] * k], [p3[0] + n3[0] * k, p3[1] + n3[1] * k], p3];
}

/** Flat polyline [x0, y0, x1, y1, …] with `n` segments along the curve. */
export function polyline(c: [P, P, P, P], n = 24): number[] {
  const [p0, p1, p2, p3] = c;
  const out: number[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n, u = 1 - t;
    const a = u * u * u, b = 3 * u * u * t, cc = 3 * u * t * t, d = t * t * t;
    out.push(a * p0[0] + b * p1[0] + cc * p2[0] + d * p3[0], a * p0[1] + b * p1[1] + cc * p2[1] + d * p3[1]);
  }
  return out;
}

/** Splits a polyline into dash pieces (`on`/`off` lengths in the same units). */
export function dashes(pts: number[], on: number, off: number): number[][] {
  const pieces: number[][] = [];
  let cur: number[] | null = null;
  let phase = 0; // distance into the current on+off period
  for (let i = 0; i + 3 < pts.length; i += 2) {
    let x0 = pts[i], y0 = pts[i + 1];
    const x1 = pts[i + 2], y1 = pts[i + 3];
    let len = Math.hypot(x1 - x0, y1 - y0);
    while (len > 1e-9) {
      const drawing = phase < on;
      const left = drawing ? on - phase : on + off - phase;
      const step = Math.min(left, len);
      const t = step / len;
      const xm = x0 + (x1 - x0) * t, ym = y0 + (y1 - y0) * t;
      if (drawing) {
        if (!cur) { cur = [x0, y0]; pieces.push(cur); }
        cur.push(xm, ym);
      } else cur = null;
      phase += step;
      if (drawing && phase >= on - 1e-9) cur = null; // dash complete
      if (phase >= on + off - 1e-9) phase = 0; // gap complete
      x0 = xm; y0 = ym; len -= step;
    }
  }
  return pieces.filter((p) => p.length >= 4);
}

/** Shortest distance from (px, py) to a polyline. */
export function distance(px: number, py: number, pts: number[]): number {
  let best = Infinity;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const x0 = pts[i], y0 = pts[i + 1], x1 = pts[i + 2], y1 = pts[i + 3];
    const vx = x1 - x0, vy = y1 - y0;
    const l2 = vx * vx + vy * vy;
    const t = l2 ? Math.max(0, Math.min(1, ((px - x0) * vx + (py - y0) * vy) / l2)) : 0;
    best = Math.min(best, Math.hypot(px - (x0 + t * vx), py - (y0 + t * vy)));
  }
  return best;
}

/** Where the connector handle of a selected card sits (right edge, outside the card). */
export function handlePoint(b: Box, zoom: number): P {
  return [b.x + b.w + 12 / zoom, b.y + b.h / 2];
}

/** Resize grip of a selected card: its bottom-left corner (C17). */
export function resizePoint(b: Box): P {
  return [b.x, b.y + b.h];
}
